import React, { useEffect, useMemo, useState } from "react";
import { Button, Checkbox, Input, Space, Typography, message } from "antd";
import type { RoadmapCompanyQuestion } from "../../types/roadmap";
import { invokeRoadmap } from "../../services/roadmapApi";

const { Text } = Typography;
const { TextArea } = Input;

const QuestionRow: React.FC<{
  row: RoadmapCompanyQuestion;
  onRefresh: () => Promise<void>;
  onRemove: () => Promise<void>;
}> = ({ row, onRefresh, onRemove }) => {
  const [text, setText] = useState(row.question);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setText(row.question);
  }, [row.id, row.question]);

  const dirty = text.trim() !== row.question.trim();
  const canSave = dirty && text.trim().length > 0;

  const saveText = async () => {
    const t = text.trim();
    if (!t) {
      message.warning("Question cannot be empty");
      return;
    }
    setSaving(true);
    try {
      await invokeRoadmap({
        action: "update_company_question",
        questionId: row.id,
        question: t,
      });
      message.success("Question updated");
      await onRefresh();
    } catch (e) {
      message.error(e instanceof Error ? e.message : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const setResolved = async (resolved: boolean) => {
    try {
      await invokeRoadmap({
        action: "update_company_question",
        questionId: row.id,
        resolved,
      });
      await onRefresh();
    } catch (e) {
      message.error(e instanceof Error ? e.message : "Update failed");
    }
  };

  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 8,
        marginBottom: 10,
        paddingBottom: 10,
        borderBottom: "1px solid #f0f0f0",
      }}
    >
      <Checkbox
        checked={row.resolved}
        style={{ marginTop: 6 }}
        onChange={(e) => void setResolved(e.target.checked)}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <TextArea
          value={text}
          onChange={(e) => setText(e.target.value)}
          autoSize={{ minRows: 2, maxRows: 8 }}
          style={{ fontSize: 13 }}
        />
        <Space size="small" style={{ marginTop: 6 }}>
          <Button type="primary" size="small" disabled={!canSave} loading={saving} onClick={() => void saveText()}>
            Save text
          </Button>
          {row.resolved && (
            <Text type="secondary" style={{ fontSize: 11 }}>
              Marked resolved
            </Text>
          )}
        </Space>
      </div>
      <Button type="link" danger size="small" onClick={() => void onRemove()} style={{ flexShrink: 0 }}>
        Remove
      </Button>
    </div>
  );
};

interface RoadmapCompanyQuestionsProps {
  itemId: string;
  questions: RoadmapCompanyQuestion[];
  onRefresh: () => Promise<void>;
}

export const RoadmapCompanyQuestions: React.FC<RoadmapCompanyQuestionsProps> = ({
  itemId,
  questions,
  onRefresh,
}) => {
  const [draft, setDraft] = useState("");
  const [adding, setAdding] = useState(false);

  const sorted = useMemo(
    () => [...questions].sort((a, b) => a.sort_order - b.sort_order),
    [questions]
  );

  const add = async () => {
    const q = draft.trim();
    if (!q) return;
    setAdding(true);
    try {
      await invokeRoadmap({ action: "add_company_question", itemId, question: q });
      message.success("Question added");
      setDraft("");
      await onRefresh();
    } catch (e) {
      message.error(e instanceof Error ? e.message : "Failed to add");
    } finally {
      setAdding(false);
    }
  };

  const remove = async (questionId: string) => {
    try {
      await invokeRoadmap({ action: "delete_company_question", questionId });
      message.success("Removed");
      await onRefresh();
    } catch (e) {
      message.error(e instanceof Error ? e.message : "Delete failed");
    }
  };

  const openCount = sorted.filter((q) => !q.resolved).length;

  return (
    <div style={{ marginBottom: 12 }}>
      <Text strong style={{ fontSize: 13 }}>
        Questions for company{" "}
        <Text type="secondary" style={{ fontWeight: "normal" }}>
          ({openCount} open)
        </Text>
      </Text>
      <Text type="secondary" style={{ display: "block", marginTop: 4, marginBottom: 8, fontSize: 12 }}>
        Open-ended items to clarify with the company or stakeholders.
      </Text>
      <div style={{ marginTop: 8 }}>
        {sorted.map((row) => (
          <QuestionRow key={row.id} row={row} onRefresh={onRefresh} onRemove={() => remove(row.id)} />
        ))}
        <TextArea
          placeholder="Add a question that needs company input…"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          autoSize={{ minRows: 2, maxRows: 6 }}
          style={{ width: "100%", fontSize: 13, marginTop: 4 }}
          onPressEnter={(e) => {
            if (e.shiftKey) return;
            e.preventDefault();
            void add();
          }}
        />
        <Button type="default" loading={adding} onClick={() => void add()} style={{ marginTop: 8 }}>
          Add question
        </Button>
      </div>
    </div>
  );
};
